/**
 * Messaging System Type Definitions
 *
 * Defines the core types for the app-wide communication system
 * supporting both toast notifications and bell/alert notifications
 */

export type MessageType = 'info' | 'success' | 'failure' | 'error' | 'warning'
export type MessageTarget = 'bell' | 'toast' | 'both'

export interface MessageCommand {
  text: string
  onClick: () => void
}

export interface Message {
  /** Unique identifier - auto-generated if not provided */
  id?: string
  /** Optional title for the message (mainly used for bell notifications) */
  title?: string
  /** Main message content */
  message: string
  /** Message type affecting styling and icons */
  type: MessageType
  /** Target destination for the message */
  target: MessageTarget
  /** Optional command button */
  command?: MessageCommand
  /** Timestamp when message was created - auto-generated */
  timestamp?: Date
  /** Read status for bell notifications */
  read?: boolean
  /** Whether toast should persist until manually dismissed */
  persistent?: boolean
  /** In-app route opened when a bell row is activated. Resolved server-side;
   *  absent for locally-produced messages, which do not navigate. */
  deepLinkPath?: string
  /** Identity of the server row this message mirrors (`ai_notifications.id`).
   *  Absent for locally-produced messages. */
  sourceId?: number
}

export interface MessagingContextValue {
  /** All messages in the system */
  messages: Message[]
  /** Messages for toast notifications */
  toastMessages: Message[]
  /** Messages for bell notifications */
  bellMessages: Message[]
  /** Count of unread bell messages */
  unreadCount: number
  /** Non-null when the bell cache could not be written to this browser. */
  persistenceWarning: string | null
  /** Add a new message. `timestamp` and `read` are honoured when supplied and
   *  defaulted to `now` / `false` otherwise. */
  addMessage: (message: Omit<Message, 'id'>) => string
  /** Patch a live message by id (e.g. coalesce a batch toast, swap
   *  success→error). Omitting `timestamp` installs a fresh one so the
   *  auto-dismiss window restarts; a supplied one is kept verbatim. No-op if
   *  the id is not present. */
  updateMessage: (id: string, patch: Partial<Omit<Message, 'id'>>) => void
  /** Mark a bell message as read */
  markAsRead: (messageId: string) => void
  /** Mark all bell messages as read */
  markAllAsRead: () => void
  /** Remove a specific message */
  removeMessage: (messageId: string) => void
  /** Remove a toast message (converts 'both' to 'bell', removes 'toast' entirely) */
  removeToastMessage: (messageId: string) => void
  /** Clear all messages */
  clearAllMessages: () => void
  /** Clear only bell messages */
  clearBellMessages: () => void
  /** Clear only toast messages */
  clearToastMessages: () => void
  /** Record the `sourceId`s of bell rows just dismissed locally, so a poll
   *  already in flight cannot resurrect them. */
  noteDismissedSourceIds: (sourceIds: readonly number[]) => void
  /** Reconcile the sticky dismissed-suppression set against the latest poll's
   *  ids: drop any id the server no longer returns (dismiss confirmed), keep the
   *  rest (POST failed/in flight). Called once per successful poll (B11). */
  reconcileDismissedSourceIds: (
    presentIds: ReadonlySet<number>,
  ) => ReadonlySet<number>
  /** Show a success toast message */
  showSuccess: (message: string) => string
  /** Show an error toast message */
  showError: (message: string) => string
  /** Show an info toast message */
  showInfo: (message: string) => string
  /** Show a warning toast message */
  showWarning: (message: string) => string
}

export interface ToastMessage extends Message {
  target: 'toast' | 'both'
}

export interface BellMessage extends Message {
  target: 'bell' | 'both'
  read: boolean
}

// Toast configuration
export interface ToastConfig {
  /** Duration in milliseconds before auto-dismiss (0 = never) */
  duration: number
  /** Maximum number of toasts to show simultaneously */
  maxToasts: number
  /** Vertical spacing between stacked toasts */
  spacing: number
}

// Default configurations
export const DEFAULT_TOAST_CONFIG: ToastConfig = {
  duration: 5000, // 5 seconds
  maxToasts: 5,
  spacing: 8, // 8px gap
}

// Bell-notification persistence configuration ---------------------------------

/**
 * localStorage key prefix for the cached bell notifications. The active tenant
 * id is appended (`messaging-bell-notifications:<tenantId>`) so one tenant's
 * notifications can never render in another tenant's bell.
 */
export const BELL_STORAGE_KEY_PREFIX = 'messaging-bell-notifications'

/** Debounce on the bell cache write, in ms. */
export const BELL_PERSIST_DEBOUNCE_MS = 300

/** Soft cap on the *serialized* bell cache, in bytes (~1 MB). Measured on the
 *  JSON payload, not a message count, since one message can be arbitrarily
 *  large. */
export const BELL_PERSIST_BYTE_BUDGET = 1_000_000

/** Surfaced when the bell cache could not be written to this browser at all.
 *  The rows on screen are intact and only the local copy was dropped, so the
 *  wording is advisory. */
export const BELL_STORAGE_OVER_BUDGET_WARNING =
  'Notifications are too large to keep a local copy in this browser. Unread notifications are kept, but this list may not survive a reload.'

export const MESSAGE_TYPE_CONFIG = {
  info: {
    color: 'info' as const,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    textColor: 'text-blue-900 dark:text-blue-100',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  success: {
    color: 'success' as const,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800',
    textColor: 'text-green-900 dark:text-green-100',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  warning: {
    color: 'warning' as const,
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
    textColor: 'text-amber-900 dark:text-amber-100',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  failure: {
    color: 'error' as const,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    textColor: 'text-red-900 dark:text-red-100',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  error: {
    color: 'error' as const,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    textColor: 'text-red-900 dark:text-red-100',
    iconColor: 'text-red-600 dark:text-red-400',
  },
}
